import h from 'hyperscript';

import { on, unByKey } from 'cadenza/utils/event-util';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import { extendUrlParameters } from 'cadenza/utils/url-param-utils';
import {
  BLANK_IMAGE_SRC,
  HEADER_DISY_CANCEL_ID,
  HEADER_DISY_MESSAGES,
  PARAMETER_CANCEL_ID
} from 'cadenza/api-client/api-constants';
import { fillUrlWithDefaultParams } from 'cadenza/api-client/api';

/**
 * Loads an image from a DataURI
 *
 * @param {object} options - Image loading parameters
 * @param {HTMLImageElement} [options.img]
 * @param {string} options.src - Data URL of the image
 * @param {AbortSignal} [options.signal] - signal for abort
 * @return {Promise<HTMLImageElement>}
 */
export function loadImage ({ img = document.createElement('img'), src, signal } = {}) {
  const eventListeners = [];
  const onAbort = () => {
    if (!img.complete) {
      // Changing the image source automatically cancels the request loading the image from the
      // previous source.
      // A blank in-memory image is preferable to null to avoid ugly JavaScript console errors.
      img.src = BLANK_IMAGE_SRC;
    }
  };
  return new Promise((resolve, reject) => {
    eventListeners.push(
      on(img, 'load', () => {
        if (img.src !== BLANK_IMAGE_SRC) {
          resolve(img);
        }
      }),
      on(img, 'error', () => reject(new Error(`Failed to load image from ${src}`)))
    );
    let url = cadenzaUrl(src);

    if (signal) {
      eventListeners.push(on(signal, 'abort', onAbort));

      if (signal.id) {
        url = extendUrlParameters(url, { [PARAMETER_CANCEL_ID]: signal.id });
      }
    }

    img.src = url;
  }).finally(() => unByKey(eventListeners));
}

/**
 * @typedef {object} ImageAndMessages
 * @property {Blob} imageBlob - The image as a Blob
 * @property {Array<object>} messages - The messages sent back from the server within 'X-Disy-Messages' response header
 */

/**
 * Loads an image using XHR and exposes the result image directly as a Blob together with server messages.
 * Uses XMLHttpRequest directly instead of our ajax() helper function because our old JQuery version
 * cannot handle blobs. Once we upgrade to at least JQuery 3 this can be changed.
 * More info: https://stackoverflow.com/questions/17657184/using-jquerys-ajax-method-to-retrieve-images-as-a-blob
 *
 * @param src - URL of the image
 * @param signal - signal for abort
 * @param mimeType - MIME type of expected result, default is 'image/png'
 * @return {Promise.<ImageAndMessages>} A promise for a Blob object representing our result.
 */
export function loadImageAsBlob (src, signal, mimeType = 'image/png') {
  src = fillUrlWithDefaultParams(src);

  const headers = new Headers({
    'Accept': mimeType,
    'X-Requested-With': 'XMLHttpRequest', // required for CSRF check on server
    ...(signal?.id ? { [HEADER_DISY_CANCEL_ID]: signal.id } : {})
  });

  return fetch(src, { headers, signal })
    .then(async response => {
      if (response.status >= 400) {
        throw new Error('Failed to load image with status ' + response.status);
      }
      const imageBlob = await response.blob();
      let messages = [];
      const messageHeader = response.headers.get(HEADER_DISY_MESSAGES);
      if (messageHeader) {
        messages = JSON.parse(messageHeader);
      }
      return { imageBlob, messages };
    });
}

/**
 * @typedef {object} ObjectUrlAndMessages
 * @property {string} objectUrl - The object URL pointing to the image
 * @property {Array<object>} messages - The messages sent back from the server within 'X-Disy-Messages' response header
 */

/**
 * Loads an image using XHR and exposes the result as an object URL.
 *
 * @param src - URL of the image
 * @param signal - signal for abort
 * @param mimeType - MIME type of expected result, default is 'image/png'
 * @return {Promise.<ObjectUrlAndMessages>} A promise for an object URL pointing to the image.
 */
export function loadImageAsObjectUrl (src, signal, mimeType = 'image/png') {
  return loadImageAsBlob(src, signal, mimeType)
    .then(({ imageBlob, messages }) => ({
      objectUrl: URL.createObjectURL(imageBlob),
      messages
    }));
}

/**
 * Loads an image using XHR and exposes the result as an img element with the src attributes set to
 * an object URL pointing to the image.
 * When the promise is resolved, the image is already loaded and the object URL has already been revoked.
 *
 * @param src - URL of the image
 * @param signal - signal for abort
 * @param mimeType - MIME type of expected result, default is 'image/png'
 * @return {Promise.<HTMLImageElement>} A promise for an img element.
 */
export function loadImageAsImgElement (src, signal, mimeType = 'image/png') {
  return new Promise((resolve, reject) => {
    loadImageAsObjectUrl(src, signal, mimeType)
      .then(({ objectUrl }) => {
        const img = h('img');
        img.onerror = () => reject(new Error(`Failed to load image from ${src}`));
        img.onload = () => {
          window.URL.revokeObjectURL(img.src);
          resolve(img);
        };
        img.src = objectUrl;
      })
      .catch(error => reject(error));
  });
}
